import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import { useTheme } from "@mui/material/styles";
import { observer } from "mobx-react-lite";
import React from "react";

type DescriptionListArgs = {
  content: Array<{
    label: string;
    value: React.ReactNode;
    below?: boolean;
    reducedPadding?: boolean;
  }>;
  dividers?: boolean;
  sx?: object;
};

/**
 * This component provides some means for the contents to be styled using the
 * MUI `sx` prop.
 *
 *  - When `below` is true, the <dt> and <dd> have the class .below. As such,
 *    they can styled by passing an `sx` object that selects them:
 *
 *      <DescriptionList
 *        sx={{
 *          "& dd.below": {
 *            width: "100%",
 *          }
 *        }}
 *        ...
 *      />
 *
 */
function DescriptionList({ content, dividers = false, sx }: DescriptionListArgs): React.ReactNode {
  const theme = useTheme();
  return (
    <Box
      component="dl"
      sx={{
        display: "grid",
        gridTemplateColumns: "auto 1fr",
        fontSize: "0.8rem",
        rowGap: 1,
        margin: 0,
        marginBottom: theme.spacing(1),
        ...sx,
      }}
    >
      {content.map(({ label, value, below = false, reducedPadding = false }, i) => (
        <React.Fragment key={i}>
          {i > 0 && dividers && (
            <Divider
              orientation="horizontal"
              sx={{
                gridColumn: "1 / span 2",
              }}
              aria-hidden="true"
              component="div"
            />
          )}
          <Box
            component="dt"
            className={below ? "below" : undefined}
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              marginRight: theme.spacing(2),
              alignSelf: "center",
              ...(reducedPadding
                ? {
                    marginTop: `-${theme.spacing(0.5)}`,
                    marginBottom: `-${theme.spacing(0.5)}`,
                  }
                : {}),
            }}
          >
            {label}
          </Box>
          <Box
            component="dd"
            className={below ? "below" : undefined}
            sx={{
              marginInlineStart: 0,
              justifySelf: "end",
              ...(reducedPadding
                ? {
                    marginTop: `-${theme.spacing(0.5)}`,
                    marginBottom: `-${theme.spacing(0.5)}`,
                  }
                : {}),
              ...(below
                ? {
                    gridColumn: "1 / span 2",
                    marginTop: "-10px",
                  }
                : {}),
            }}
          >
            {value}
          </Box>
        </React.Fragment>
      ))}
    </Box>
  );
}

/**
 * General-purpose component that abstracts over the <dl>, <dd>, and <dt> tags.
 *
 * This component MUST ONLY have a dependency on this directory and ../util.
 */
export default observer(DescriptionList);
